// Import at the top
import { getDistanceMeters } from './distance';

/**
 * Load Google Maps JavaScript API if not already loaded
 */
export function loadGoogleMapsApi(apiKey: string): Promise<void> {
    return new Promise((resolve, reject) => {
        if (window.google && window.google.maps && window.google.maps.DirectionsService) {
            resolve();
            return;
        }

        const scriptId = 'google-maps-js';
        if (document.getElementById(scriptId)) {
            // Already loading
            (window as any).gmapsLoadedCallback = () => resolve();
            return;
        }

        (window as any).gmapsLoadedCallback = () => resolve();
        const script = document.createElement('script');
        script.id = scriptId;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=gmapsLoadedCallback`;
        script.async = true;
        script.onerror = reject;
        document.body.appendChild(script);
    });
}

/**
 * Fetch road ways within a polygon using Overpass API
 */
export async function fetchRoadWaysInPolygon(
    polygon: [number, number][],
    roadTypes: string[]
): Promise<[number, number][][]> {
    const polyCoords = polygon.map(([lat, lon]) => `${lat} ${lon}`).join(' ');
    const typeFilter = roadTypes.length > 0 ? `"highway"~"${roadTypes.join('|')}"` : '"highway"';
    
    // Query for all highway ways within the polygon
    const query = `[out:json][timeout:60];way[${typeFilter}](poly:"${polyCoords}");out geom;`;
    const response = await fetch('https://overpass-api.de/api/interpreter', {
        method: 'POST',
        body: query,
        headers: { 'Content-Type': 'text/plain' },
    });
    
    const data = await response.json();
    // Each way's geometry is an array of {lat, lon}
    return (data.elements || []).map((el: any) => 
        (el.geometry || []).map((g: any) => [g.lat, g.lon] as [number, number])
    );
}

/**
 * Sample points along a way at regular intervals
 */
export function samplePointsAlongWay(
    way: [number, number][],
    interval: number
): [number, number][] {
    if (way.length < 2) return [];
    
    const points: [number, number][] = [];
    let dist = 0;
    
    for (let i = 1; i < way.length; i++) {
        const [lat1, lon1] = way[i - 1];
        const [lat2, lon2] = way[i];
        const segLen = getDistanceMeters([lat1, lon1], [lat2, lon2]);
        let segStart = 0;
        
        while (dist + segLen - segStart >= interval) {
            const frac = (interval - segStart) / segLen;
            const lat = lat1 + (lat2 - lat1) * frac;
            const lon = lon1 + (lon2 - lon1) * frac;
            points.push([lat, lon]);
            segStart += interval;
        }
        
        dist = (dist + segLen - segStart) % interval;
    }
    
    return points;
}

/**
 * Find the nearest node to a point from a list of nodes
 */
export function findNearestNode(
    point: [number, number],
    nodes: [number, number][]
): [number, number] {
    let minDist = Infinity;
    let nearest: [number, number] = point;
    
    for (const node of nodes) {
        const d = getDistanceMeters(point, node);
        if (d < minDist) {
            minDist = d;
            nearest = node;
        }
    }
    
    return nearest;
} 